import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// All roles that can access the Empreendimentos module
const EMP_ALLOWED_ROLES = [
  'admin', 'coadmin', 'admin_cluster',
  'gestor', 'gestor_planejamento', 'gestor_qualidade',
  'gestor_documentacao', 'gestor_execucao',
  'apontador', 'executor', 'observador',
];

/**
 * Hook para verificar se o módulo Empreendimentos está ativo
 * para os clusters do usuário logado.
 * 
 * Feature flag OFF = módulo completamente invisível.
 * Fallback: se NÃO existir nenhum flag, módulo é visível (enterprise default).
 */
export function useEmpFeatureFlag() {
  const { user, role, gestorClusterIds } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['emp-feature-flags', user?.id, role],
    queryFn: async () => {
      const { data: flags, error } = await supabase
        .from('emp_feature_flags')
        .select('cluster_id')
        .eq('modulo', 'empreendimentos')
        .eq('ativo', true);

      if (error) {
        console.error('Error fetching emp feature flags:', error);
        return { clusters: [], isEmpty: true };
      }

      const isEmpty = !flags || flags.length === 0;

      // Enterprise fallback: no flags → fetch ALL clusters so queries can run
      if (isEmpty) {
        const { data: allClusters, error: clErr } = await supabase
          .from('clusters')
          .select('id');
        if (clErr) {
          console.error('Error fetching clusters for fallback:', clErr);
          return { clusters: [], isEmpty: true };
        }
        return {
          clusters: (allClusters || []).map(c => c.id),
          isEmpty: true,
        };
      }

      return {
        clusters: flags.map(f => f.cluster_id),
        isEmpty: false,
      };
    },
    enabled: !!user && !!role && EMP_ALLOWED_ROLES.includes(role || ''),
    staleTime: 5 * 60 * 1000,
  });

  const activeClusterIds = data?.clusters ?? [];
  const noFlagsConfigured = data?.isEmpty ?? true;

  // Module visibility logic:
  // 1. Role must be in allowed list
  // 2. If no feature flags exist at all → enterprise fallback → visible
  // 3. If flags exist, check cluster match
  const isModuleVisible = (() => {
    if (!role || !EMP_ALLOWED_ROLES.includes(role)) return false;
    
    // Enterprise fallback: no flags configured = module visible for all allowed roles
    if (noFlagsConfigured) return true;
    
    // Admin/CoAdmin: visible if ANY cluster has it active
    if (role === 'admin' || role === 'coadmin') return activeClusterIds.length > 0;
    
    // Gestor roles: check if their clusters overlap
    if (role === 'gestor') {
      return gestorClusterIds.some(cid => activeClusterIds.includes(cid));
    }
    
    // All other operational roles: visible (cluster filtering handled by RLS)
    return true;
  })();

  return {
    isModuleVisible,
    activeClusterIds,
    isLoading,
  };
}
